import { notification } from 'antd';
import type { NotificationInstance } from 'antd/es/notification/interface';
import React, { ReactNode } from 'react';
import Image from 'next/image';

import checkCircle from '../../public/assets/notification/check_circle.svg';
import grayCheckCircle from '../../public/assets/notification/gray_check_circle.svg';

// Inline base64 encoded SVG for info_circle to work offline
const infoCircleBase64 = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzUiIGhlaWdodD0iMzYiIHZpZXdCb3g9IjAgMCAzNSAzNiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTE3LjUgMC41MzEyNTJDMjcuMTMzIDAuNTMxMjUzIDM0Ljk2ODggOC4zNjcgMzQuOTY4OCAxOEMzNC45Njg4IDI3LjYzMyAyNy4xMzMgMzUuNDY4NyAxNy41IDM1LjQ2ODdDNy44NjcgMzUuNDY4NyAwLjAzMTI0NjkgMjcuNjMzIDAuMDMxMjQ3NyAxOEMwLjAzMTI0ODYgOC4zNjcgNy44NjcgMC41MzEyNTEgMTcuNSAwLjUzMTI1MlpNMTcuNSAzMy4wMzEyQzI1Ljc4OTEgMzMuMDMxMiAzMi41MzEzIDI2LjI4OTEgMzIuNTMxMyAxOEMzMi41MzEzIDkuNzEwODggMjUuNzg5MSAyLjk2ODc1IDE3LjUgMi45Njg3NUM5LjIxMDg4IDIuOTY4NzUgMi40Njg3NSA5LjcxMDg3IDIuNDY4NzUgMThDMi40Njg3NSAyNi4yODkxIDkuMjEwODcgMzMuMDMxMiAxNy41IDMzLjAzMTJaTTE2LjI4MTMgMTAuNjg3NUwxNi4yODEyIDE4LjExNTRDMTYuMjgxMiAxOC43ODgyIDE2LjgyNzMgMTkuMzM0MiAxNy41IDE5LjMzNDJDMTguMTcyNyAxOS4zMzQyIDE4LjcxODcgMTguNzg4MiAxOC43MTg3IDE4LjExNTRMMTguNzE4OCAxMC42ODc1QzE4LjcxODggMTAuMDE0OCAxOC4xNzI4IDkuNDY4NzUgMTcuNSA5LjQ2ODc1QzE2LjgyNzMgOS40Njg3NSAxNi4yODEzIDEwLjAxNDcgMTYuMjgxMyAxMC42ODc1Wk0xNS44NDI1IDIzLjY4NzVDMTUuODQyNSAyNC41ODQ1IDE2LjU2ODggMjUuMzEyNSAxNy40Njc1IDI1LjMxMjVMMTcuNDgzNyAyNS4zMTI1QzE4LjM4MDcgMjUuMzEyNSAxOS4xMDA4IDI0LjU4NDUgMTkuMTAwOCAyMy42ODc1QzE5LjEwMDggMjIuNzkwNSAxOC4zNjQ1IDIyLjA2MjUgMTcuNDY3NSAyMi4wNjI1QzE2LjU3MDUgMjIuMDYyNSAxNS44NDI1IDIyLjc5MDUgMTUuODQyNSAyMy42ODc1WiIgZmlsbD0iI0MxMDUwNSIvPgo8L3N2Zz4K';

type NotificationType = 'success' | 'info' | 'warning' | 'error' | 'api_error';

let _notificationAPI: NotificationInstance | null = null;

export const setNotificationAPI = (api: NotificationInstance | null) => {
    _notificationAPI = api;
};

export const showNotification = (
    type: NotificationType,
    message: string,
    description: string,
    duration: number = 3,
    className?: string,
    icon?: ReactNode // Add icon as an optional parameter
) => {
    // Check if we're in a browser environment
    if (typeof window === 'undefined' || typeof document === 'undefined') {
        // Fallback for non-browser environments (like Node.js tests)
        console.log(`[NOTIFICATION] ${type.toUpperCase()}: ${message} - ${description}`);
        return;
    }

    let ICONS: { [key in NotificationType]?: ReactNode } = {
        success: React.createElement(Image, { src: checkCircle, alt: "success", height: 26, width: 26 }),
        info: React.createElement(Image, { src: grayCheckCircle, alt: 'info', height: 26, width: 26 }),
        api_error: React.createElement(Image, { src: infoCircleBase64, alt: 'api_error', height: 39, width: 39 }),
    }

    const getIcon = (type: NotificationType) => {
        return ICONS[type] || "";
    };

    let finalIcon = icon || getIcon(type);

    const antdType: Exclude<NotificationType, 'api_error'> =
        type === 'api_error' ? 'error' : type;

    const api = _notificationAPI ?? notification;
    api[antdType]({
        title: message,
        description,
        duration,
        className: `global-notification notification-${type} ${className || ''}`.trim(),
        ...(finalIcon && { icon: finalIcon })
    });
};
